// Imports => React
import { useEffect, useMemo, useState } from "react";
import { withStore } from "@stores";
import { observer } from "mobx-react-lite";
import clsx from "clsx";
import styles from "./ac-refund-modal.module.scss";
import { KEYS as MPKEYS } from "@constants";
// Imports => Constants
import {
  AcCard,
  AcIsSet,
  AcLoaderInline,
  KEYS,
  THEMES,
  TYPES,
  ICONS,
  AcCheckbox,
  AcTextInput,
  VISUALS,
  AcCapitalize,
  AcSelectBox,
  AcIsObject,
  AcHeading,
} from "@opp/ui-kit";

// Imports => Atoms
import {
  AcIcon,
  AcTooltip,
  AcContainer,
  AcRow,
  AcColumn,
  AcRichContent,
  AcButton,
} from "@opp/ui-kit";
import { AcFormatCurrencyAmount } from "../../utilities";
import { AcFormatCurrencySeparator } from "../../utilities";
import { useFormActions } from "../../hooks";
import { withTranslation } from "react-i18next";
import getSymbolFromCurrency from "currency-symbol-map";

const _CLASSES = {
  MAIN: "ac-refund-modal ac-modal--size-lg",
  CONTENT: "ac-refund-modal__content",
};

const AcRefundModal = ({
  store: { ui, transactions, profile, settings },
  transaction,
  submitRefund,
  submitTopup,
  i18n,
  t,
}) => {
  const { current_refundable } = transactions;
  const { current_partner_currency } = profile;
  const { configurations } = settings;

  const [fields, setFields] = useState({
    amount: 0,
    amount_decimal: 0,
    topup_payment_method: null,
    ideal_issuer: null,
  });

  const [errors, setErrors] = useState({
    amount: null,
    amount_decimal: null,
    topup_payment_method: null,
    ideal_issuer: null,
  });

  const { hasErrors, handleInputChange, handleInputValidation } =
    useFormActions({
      t,
      fields,
      setFields,
      errors,
      setErrors,
    });

  const getAmountsFromMaximum = useMemo(() => {
    if (!AcIsSet(current_refundable)) {
      return [0, 0];
    }

    const amount = current_refundable.maximum_refundable_amount
      .toString()
      .slice(0, -2);
    const amountDecimal = current_refundable.maximum_refundable_amount
      .toString()
      .slice(-2);

    return [amount, amountDecimal];
  }, [current_refundable]);

  useEffect(() => {
    let maxAmount;
    let maxAmountDecimal;

    [maxAmount, maxAmountDecimal] = getAmountsFromMaximum;
    setFields((prevState) => ({
      ...prevState,
      amount: maxAmount,
      amount_decimal: maxAmountDecimal,
    }));
  }, [getAmountsFromMaximum]);

  useEffect(() => {
    transactions.get_refundable(t, transaction.uid);
  }, [transaction.uid]);

  const handleClose = (event) => {
    if (event && event.preventDefault) {
      event.preventDefault();
    }

    ui.reset(KEYS.MODAL, false);
  };

  const handleSubmitRefundConfirm = () => {
    return new Promise(async (resolve, reject) => {
      if (submitRefund) {
        let amount = fields.amount.toString();
        let amountDec = fields.amount_decimal.toString();
        if (amountDec.length === 1) {
          amountDec = amountDec + "0";
        }

        const data = {
          amount: parseInt(amount + amountDec),
        };
        submitRefund(t, transaction.uid, data)
          .then(() => {
            transactions.get_by_id(t, transaction.uid, {
              expand: [
                "statuses",
                "escrow",
                "merchantObject",
                "merchantProfileObject",
                "partnerObject",
                "refunds",
              ],
            });
            resolve();
            ui.setValue(KEYS.MODAL, KEYS.VISIBLE, false);
          })
          .catch(() => {
            reject();
          });
      }
    });
  };

  const handleSubmitRefund = (event) => {
    if (event && event.preventDefault) {
      event.preventDefault();
    }
    if (event && event.persist) {
      event.persist();
    }

    let amountDec = fields.amount_decimal
      ? fields.amount_decimal.toString()
      : "0";
    if (amountDec.length === 1) {
      amountDec = amountDec + "0";
    }

    let amount = fields.amount ? fields.amount.toString() : "0";

    amount = parseInt(amount + amountDec);

    let amountFormatted = AcFormatCurrencyAmount(
      amount,
      current_partner_currency,
      i18n.resolvedLanguage,
      true
    );

    if (isNaN(amount)) {
      amountFormatted = "?";
    }

    let amount_total = AcFormatCurrencyAmount(
      current_refundable.maximum_refundable_amount,
      current_partner_currency,
      i18n.resolvedLanguage,
      true
    );

    ui.setInstance(KEYS.CONFIRM_MODAL, {
      callback: () => {
        ui.setValue(KEYS.CONFIRM_MODAL, KEYS.VISIBLE, false);
      },
      visible: true,
      props: {
        content: `${t("refund-confirm", {
          amount: amountFormatted,
          amount_total: amount_total,
          namespace: "dashboard",
        })}`,
        cancel: {
          label: t("cancel", { ns: "common" }),
          callback: () => {
            ui.setValue(KEYS.CONFIRM_MODAL, KEYS.VISIBLE, false);
          },
        },
        confirm: {
          label: t("confirm", { ns: "common" }),
          callback: handleSubmitRefundConfirm,
        },
      },
    });
  };

  const handleSubmitTopup = (event) => {
    if (event && event.preventDefault) {
      event.preventDefault();
    }
    if (event && event.persist) {
      event.persist();
    }

    if (submitTopup) {
      let amount = fields.amount.toString();
      let amountDec = fields.amount_decimal.toString();
      if (amountDec.length === 1) {
        amountDec = amountDec.toString() + "0";
      }

      const ideal_issuer =
        fields.topup_payment_method === "ideal" ? fields.ideal_issuer : null;
      const country = null;

      const data = {
        amount: parseInt(amount.toString() + amountDec.toString()),
        payment_method: fields.topup_payment_method,
        issuer: ideal_issuer,
        country: country,
        locale: i18n.resolvedLanguage.slice(0, 2),
        return_url: window.location.href,
      };
      submitTopup(t, transaction.uid, data).then((response) => {
        location.href = response.redirect_url;
      });
    }
  };

  const getSubmitRefundButtonOptions = useMemo(() => {
    return {
      type: TYPES.SUBMIT,
      theme: THEMES.OMEGA,
      disabled:
        hasErrors ||
        transactions.is_busy ||
        (current_refundable && current_refundable.has_active_topup),
      title: `${t("refund.create-refund", "Create refund", {
        ns: "transactions",
      })}`,
      callback: handleSubmitRefund,
      loading: transactions.is_busy,
    };
  }, [
    fields,
    errors,
    hasErrors,
    handleSubmitRefund,
    transactions,
    transactions.is_busy,
    current_refundable,
  ]);

  const getSubmitTopupButtonOptions = useMemo(() => {
    let actualDecimals = fields.amount_decimal.toString();
    if (actualDecimals.length === 1) {
      actualDecimals = actualDecimals + "0";
    }

    return {
      type: TYPES.SUBMIT,
      theme: THEMES.OMEGA,
      disabled:
        hasErrors ||
        transactions.is_busy ||
        (fields.topup_payment_method === "ideal" &&
          !fields.ideal_issuer &&
          !settings.settings.ideal_skip_issuer_selection) ||
        !fields.topup_payment_method ||
        (current_refundable && current_refundable.has_active_topup) ||
        ~~fields.amount * 100 + ~~actualDecimals >
          current_refundable?.maximum_refundable_amount,
      title: `${t("refund.refund", "Refund", { ns: "transactions" })}`,
      callback: handleSubmitTopup,
      loading: transactions.is_busy,
      className: "h-margin-left-5",
    };
  }, [
    fields,
    errors,
    hasErrors,
    handleSubmitTopup,
    transactions,
    transactions.is_busy,
    current_refundable,
  ]);

  const getContentClassNames = useMemo(() => {
    return clsx([_CLASSES.CONTENT]);
  }, []);

  const getStyleClassNames = useMemo(() => {
    return clsx([_CLASSES.MAIN]);
  }, []);

  const getCloseButtonOptions = useMemo(() => {
    return {
      type: TYPES.BUTTON,
      theme: THEMES.LIGHT,
      title: `${t("cancel", "Cancel", { ns: "common" })}`,
      callback: handleClose,
    };
  }, [handleClose]);

  const getInputsAreReadOnly = useMemo(() => {
    return transaction && transaction.status === "reserved";
  }, [transaction, transaction.status]);

  const getAmountInputOptions = useMemo(() => {
    return {
      type: TYPES.NUMERIC,
      name: "amount",
      value: fields.amount,
      callback: handleInputChange,
      validation: handleInputValidation,
      required: true,
      tabIndex: 0,
      focus: true,
      readonly: getInputsAreReadOnly,
      className: "ac-refund-modal__input-amount",
    };
  }, [fields, fields.amount]);

  const getAmountDecimalInputOptions = useMemo(() => {
    return {
      type: TYPES.NUMERIC,
      name: "amount_decimal",
      value: fields.amount_decimal,
      callback: handleInputChange,
      validation: handleInputValidation,
      required: true,
      tabIndex: 1,
      focus: false,
      readonly: getInputsAreReadOnly,
      className: "ac-refund-modal__input-amount-decimal",
    };
  }, [fields, fields.amount_decimal]);

  const renderAmountInputs = useMemo(() => {
    if (!AcIsSet(current_refundable) || transactions.is_loading_refundable) {
      return <AcLoaderInline loading />;
    }

    return (
      <>
        <AcCard
          className={
            "ac-profile__column ac-profile__column--wide h-padding-x-0 h-padding-bottom-0 h-margin-bottom-0"
          }
          theme={THEMES.TRANSPARENT}
        >
          <AcContainer>
            <AcRow className={"h-text-align-center"}>
              <AcColumn xss={12} xs={12} md={12} className={"h-margin-top-30"}>
                <AcHeading rank={3} theme={THEMES.PITCH}>
                  {t("refund.title", "Refund amount", { ns: "transactions" })}
                </AcHeading>
              </AcColumn>
            </AcRow>
            <AcRow className={"h-justify-content-center h-align-items-center"}>
              <span className={"ac-refund-modal__currency-symbol"}>
                {getSymbolFromCurrency(current_partner_currency)}
              </span>

              <AcTextInput {...getAmountInputOptions} />

              <span className={"ac-refund-modal__currency-separator"}>
                {AcFormatCurrencySeparator(i18n.resolvedLanguage)}
              </span>

              <AcTextInput {...getAmountDecimalInputOptions} />
            </AcRow>
            <AcRow className={"h-text-align-center"}>
              <AcColumn xss={12} xs={12} md={12} className={"h-margin-y-0"}>
                {/* todo currency*/}
                <span className={"h-text--color-medium"}>{`${t(
                  "refund.max",
                  "Max available",
                  { ns: "transactions" }
                )}: ${AcFormatCurrencyAmount(
                  current_refundable.maximum_refundable_amount,
                  current_partner_currency,
                  i18n.resolvedLanguage,
                  true
                )}`}</span>
                <AcTooltip
                  content={t("refund.non-refundable-fees-tooltip", {
                    ns: "transactions",
                  })}
                  trigger="hover"
                  className="h-margin-left-10 h-v-align-middle d-md-inline d-none"
                  placement="top"
                >
                  <span>
                    <AcIcon
                      icon={ICONS.INFO_OUTLINED}
                      className={`h-text--color-medium ${styles.styledTooltipFont}`}
                    />
                  </span>
                </AcTooltip>
              </AcColumn>
            </AcRow>
          </AcContainer>
        </AcCard>
      </>
    );
  }, [
    transactions,
    transactions.is_loading_refundable,
    current_refundable,
    fields,
    fields.amount,
    fields.amount_decimal,
    current_partner_currency,
    i18n.resolvedLanguage,
  ]);

  const getPaymentMethodName = (payment_method) => {
    switch (payment_method) {
      case "bcmc":
        return "Bancontact";
      case "ideal":
        return "iDEAL";
      case "pi-single":
        return t("payment-methods.pi-single", { namespace: "common" });
      default:
        return AcCapitalize(payment_method);
    }
  };
  const getPaymentMethodIcon = (payment_method) => {
    let Icon = null;

    switch (payment_method) {
      case "bcmc":
        Icon = VISUALS.PAYMENT_METHODS.BANCONTACT;
        break;
      case "creditcard":
        Icon = VISUALS.PAYMENT_METHODS.CREDITCARD;
        break;
      case "ideal":
        Icon = VISUALS.PAYMENT_METHODS.IDEAL;
        break;
      case "pi-single":
        Icon = VISUALS.PAYMENT_METHODS.PIS;
        break;
      default:
    }

    if (!AcIsSet(Icon)) {
      return null;
    }
    return (
      <img
        src={Icon}
        className={`h-margin-left-10 d-flex ${
          "ac-refund-modal__payment-method-icon-" + payment_method
        }`}
        title={`${t("payment-method", "Payment method", {
          ns: "common",
        })}: ${payment_method}`}
        alt={`${t("payment-method", "Payment method", {
          ns: "common",
        })}: ${payment_method}`}
        rel={payment_method}
      />
    );
  };

  const renderExtraInput = (payment_method) => {
    if (
      payment_method.name === "ideal" &&
      !settings.settings.ideal_skip_issuer_selection
    ) {
      const issuers = [];
      for (const [value, name] of Object.entries(payment_method.issuers)) {
        issuers.push({ name: name, value: value });
      }

      const display =
        fields.topup_payment_method === "ideal" ? "inherit" : "none";
      return (
        <div style={{ display: display }}>
          <AcSelectBox
            callback={handleInputChange}
            label={""}
            placeholder={t("refund.issuer", "Issuer", { ns: "transactions" })}
            options={issuers}
            value={fields.ideal_issuer}
            name={"ideal_issuer"}
            ellipseLongChosenOption
            className={"h-margin-y-0"}
          />
        </div>
      );
    }

    return "";
  };

  const renderPaymentMethod = (payment_method) => {
    return (
      <AcCard
        className={`ac-profile__column ac-profile__column--wide ${
          payment_method.name === fields.topup_payment_method
            ? "ac-refund-modal__payment-method-container-selected"
            : ""
        }`}
        theme={THEMES.EGGWHITE}
      >
        <AcContainer>
          <AcRow style={{ alignItems: "center" }}>
            <AcColumn xxs={4} md={3} lg={3} xl={3} className={"h-margin-y-0"}>
              <AcCheckbox
                id={`topup-${payment_method.name}`}
                className={"ac-refund-modal__payment-method-radio"}
                name={"topup_payment_method"}
                value={payment_method.name}
                checked={payment_method.name === fields.topup_payment_method}
                callback={handleInputChange}
                validation={handleInputValidation}
                type={TYPES.RADIO}
              >
                {getPaymentMethodIcon(payment_method.name)}
              </AcCheckbox>
            </AcColumn>
            <AcColumn xxs={8} md={3} lg={3} xl={3} className={"h-margin-y-0"}>
              <AcColumn
                xxs={12}
                md={12}
                lg={12}
                xl={12}
                className={"h-margin-y-0"}
                style={{whiteSpace: 'nowrap'}}
              >
                <b>{getPaymentMethodName(payment_method.name)}</b>
              </AcColumn>
              <AcColumn
                xxs={12}
                md={12}
                lg={12}
                xl={12}
                className={"h-margin-y-0"}
              >
                {/*todo fees are not multicurrency yet. Isnt fee always in euros?*/}
                <span>
                  {" "}
                  +{" "}
                  {AcFormatCurrencyAmount(
                    payment_method.fee.amount,
                    payment_method.fee.currency,
                    i18n.resolvedLanguage,
                    true
                  )}
                </span>
              </AcColumn>
            </AcColumn>
            <AcColumn xxs={12} md={6} lg={6} xl={6} className={"h-margin-y-0"}>
              {renderExtraInput(payment_method)}
            </AcColumn>
          </AcRow>
        </AcContainer>
      </AcCard>
    );
  };

  const renderTopup = useMemo(() => {
    if (!AcIsSet(current_refundable) || transactions.is_loading_refundable) {
      return <></>;
    }

    if (current_refundable.has_active_topup) {
      return (
        <>
          <AcCard
            className={
              "ac-profile__column ac-profile__column--wide h-padding-x-0 h-padding-bottom-0 h-margin-bottom-0"
            }
            theme={THEMES.TRANSPARENT}
          >
            <AcContainer>
              <AcRow className={"h-justify-content-center"}>
                <AcRichContent
                  content={`<p>${t(
                    "refund.has-active-topup",
                    "Transaction has an active topup.",
                    { ns: "transactions" }
                  )}</p>`}
                />
              </AcRow>
            </AcContainer>
          </AcCard>
        </>
      );
    }

    const validationText = () => {
      if (
        fields.topup_payment_method === "ideal" &&
        !settings.settings.ideal_skip_issuer_selection
      )
        return t("refund.select-ideal-issuer", { ns: "transactions" });
      if (!fields.topup_payment_method)
        return t("refund.select-topup-payment-method", { ns: "transactions" });
      return "";
    };

    return (
      <div className={"ac-refund-modal__topup-container"}>
        <div className={"ac-refund-modal__topup-content"}>
          <AcRichContent
            className={
              "h-text-align-center h-margin-y-20 h-text--color-error ac-refund-modal__topup-content-text"
            }
            content={`<p>${validationText()}</p>`}
          />
          {current_refundable.payment_methods.map((payment_method) => {
            return renderPaymentMethod(payment_method);
          })}
        </div>
      </div>
    );
  }, [
    transactions,
    transactions.is_loading_refundable,
    current_refundable,
    fields,
  ]);

  const getTextByCanNotRefundReason = useMemo(() => {
    if (!AcIsSet(current_refundable) || transactions.is_loading_refundable) {
      return <AcLoaderInline loading />;
    }

    let text = "";
    switch (current_refundable.can_not_refund_reason) {
      case MPKEYS.NOT_REFUNDABLE_REASON_STATUS:
        text = `${t(
          "refund.not-refundable-reason." + MPKEYS.NOT_REFUNDABLE_REASON_STATUS,
          { ns: "transactions" }
        )}`;
        break;
      case MPKEYS.NOT_REFUNDABLE_REASON_ALREADY_REFUNDED:
        text = `${t(
          "refund.not-refundable-reason." +
            MPKEYS.NOT_REFUNDABLE_REASON_ALREADY_REFUNDED,
          { ns: "transactions" }
        )}`;
        break;
      case MPKEYS.NOT_REFUNDABLE_REASON_OTHER:
      default:
        text = `${t(
          "refund.not-refundable-reason." + MPKEYS.NOT_REFUNDABLE_REASON_OTHER,
          { ns: "transactions" }
        )}`;
    }

    return (
      <AcRow className={"h-margin-top-10 h-margin-bottom-100"}>
        <AcColumn>
          <AcRichContent
            theme={THEMES.MEDIUM}
            content={`<p>${text}</p>`}
            className={"h-text--color-medium"}
          />
        </AcColumn>
      </AcRow>
    );
  }, [current_refundable, transactions.is_loading_refundable]);

  const isPaypalTransaction = useMemo(() => {
    return ["paypal", "paypal-pc"].includes(transaction.payment_method);
  }, [transaction, transaction.paymeant_method]);

  const isTopupWithoutConfigurations = useMemo(() => {
    return (
      current_refundable &&
      current_refundable.can_refund &&
      current_refundable.show_topup &&
      !configurations.topups
    );
  }, [configurations.topups, current_refundable]);

  const getIntroText = useMemo(() => {
    if (!AcIsSet(current_refundable) || !AcIsObject(current_refundable)) {
      return <></>;
    }

    if (isPaypalTransaction) {
      return (
        <>
          <a
            href="https://www.paypal.com/"
            target="_blank"
            onClick={() => {
              window.open("https://www.paypal.com/", "_blank").focus();
            }}
            rel="noreferrer"
          >
            {t(
              "refund.paypal.login-link-text",
              "Log in your PayPal merchant account to refund this transaction.",
              { ns: "transactions" }
            )}
          </a>
          <p>
            {t(
              "refund.paypal.intro",
              "Transactions paid with PayPal can only be refunded by initiating a refund via PayPal.com. Please use the link below and login to PayPal and follow the following steps to complete refund",
              { ns: "transactions" }
            )}
            :
          </p>
          <ul>
            <li>
              {t(
                "refund.paypal.li-1",
                "Click menu item: History -> Basic search",
                { ns: "transactions" }
              )}
            </li>
            <li>
              {t("refund.paypal.li-2", "Click 'Find a transaction'", {
                ns: "transactions",
              })}
            </li>
            <li>
              {t("refund.paypal.li-3", "Find the transaction", {
                ns: "transactions",
              })}
            </li>
            <li>
              {t(
                "refund.paypal.li-4",
                "Click 'Issue a refund' and follow the steps.",
                { ns: "transactions" }
              )}
            </li>
          </ul>
          <p>
            {t(
              "refund.paypal.outro",
              "Successful refunds initiated from PayPal will be listed here and the transaction will receive the status 'refunded'.",
              { ns: "transactions" }
            )}
          </p>
        </>
      );
    }

    if (!current_refundable.can_refund) {
      return <></>;
    }

    return (
      <AcRichContent
        theme={THEMES.MEDIUM}
        content={`<p>${t("refund.intro", { ns: "transactions" })}</p>`}
        className={"h-text--color-medium"}
      />
    );
  }, [isPaypalTransaction, current_refundable]);

  return (
    <div className={getStyleClassNames}>
      <div className={getContentClassNames}>
        <AcContainer fluid>
          <AcRow>
            <AcColumn>{getIntroText}</AcColumn>
          </AcRow>
          {current_refundable &&
          current_refundable.can_refund &&
          !isPaypalTransaction &&
          !isTopupWithoutConfigurations ? (
            <>
              {renderAmountInputs}

              {current_refundable &&
              current_refundable.can_refund &&
              configurations.topups &&
              current_refundable.show_topup ? (
                <>{renderTopup}</>
              ) : (
                ""
              )}
            </>
          ) : (
            <>{getTextByCanNotRefundReason}</>
          )}

          <AcRow className={"h-margin-top-20"}>
            <AcColumn
              xs={5}
              sm={6}
              className={"h-text--align-left h-flex-v-align-center"}
            >
              <AcButton {...getCloseButtonOptions}>
                <span>{t("cancel", "Cancel", { ns: "common" })}</span>
              </AcButton>
            </AcColumn>

            {current_refundable &&
            current_refundable.can_refund &&
            !isPaypalTransaction &&
            !isTopupWithoutConfigurations ? (
              <AcColumn xs={7} sm={6} className={"h-text--align-right"}>
                {current_refundable &&
                current_refundable.can_refund &&
                current_refundable.show_topup ? (
                  <AcButton {...getSubmitTopupButtonOptions}>
                    <span>
                      {t("refund.refund", "Refund now", { ns: "transactions" })}
                    </span>
                  </AcButton>
                ) : (
                  <AcButton {...getSubmitRefundButtonOptions}>
                    <span>
                      {t("refund.refund", "Refund now", { ns: "transactions" })}
                    </span>
                  </AcButton>
                )}
              </AcColumn>
            ) : (
              ""
            )}
          </AcRow>
        </AcContainer>
      </div>
    </div>
  );
};

export default withTranslation(["transactions", "common", "store"])(
  withStore(observer(AcRefundModal))
);
